import type { Event, EventHint } from "@sentry/core";
import {
  isStaleChunkError,
  reloadForStaleChunk,
  wasStaleChunkReloadTriggered
} from "./stale-chunk-error";

const RESIZE_OBSERVER_LOOP_LIMIT_PATTERN = /ResizeObserver loop limit/i;
const RESIZE_OBSERVER_LOOP_COMPLETED_PATTERN = /ResizeObserver loop completed/i;
const HTTP_ERROR_RESPONSE_PATTERN = /HttpErrorResponse/i;
const HTTP_FAILURE_STATUS_ZERO_PATTERN = /Http failure response for .+: 0\b/i;
const HTTP_FAILURE_STATUS_IGNORABLE_PATTERN =
  /Http failure response for .+: (401|403|404)\b/i;
// 5xx are reported once by Sentry.httpClientIntegration (grouped per
// endpoint); the Angular HttpErrorResponse for the same call is a duplicate.
const HTTP_FAILURE_STATUS_SERVER_ERROR_PATTERN =
  /Http failure response for .+: 5\d\d\b/i;
// Network failure of a fetch, as worded by Chrome ("Failed to fetch"),
// Firefox ("NetworkError when attempting to fetch resource.") and Safari
// ("Load failed", only as a TypeError: the text alone is too generic).
const FAILED_TO_FETCH_PATTERN = /Failed to fetch/i;
const FIREFOX_NETWORK_ERROR_PATTERN =
  /NetworkError when attempting to fetch resource/i;
const SAFARI_LOAD_FAILED_PATTERN = /^Load failed\b/i;
const ABORT_ERROR_NAME = "AbortError";
const SSE_NETWORK_ERROR_PATTERN = /TypeError:\s*network error/i;

function collectErrorMessages(event: Event, hint: EventHint): string[] {
  const messages: string[] = [];
  const error: unknown = hint?.originalException;

  if (error instanceof Error && error.message) {
    messages.push(error.message);
  } else if (typeof error === "string") {
    messages.push(error);
  }

  const exceptionValues = event.exception?.values;
  if (exceptionValues) {
    for (const exception of exceptionValues) {
      if (exception.value) {
        messages.push(exception.value);
      }
    }
  }

  return messages;
}

function messageMatchesBenignNetworkFailure(message: string): boolean {
  if (FAILED_TO_FETCH_PATTERN.test(message)) {
    return true;
  }

  if (FIREFOX_NETWORK_ERROR_PATTERN.test(message)) {
    return true;
  }

  if (SSE_NETWORK_ERROR_PATTERN.test(message)) {
    return true;
  }

  return false;
}

/**
 * A request cancelled on purpose (navigation, newer search, unsubscribe):
 * Angular's FetchBackend calls `AbortController.abort()` and the rejected
 * fetch surfaces as an unhandled `AbortError` (SNT-12452).
 */
function isAbortError(event: Event, hint: EventHint): boolean {
  const error: unknown = hint?.originalException;

  if (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === ABORT_ERROR_NAME
  ) {
    return true;
  }

  return (
    event.exception?.values?.some(
      (exception) => exception.type === ABORT_ERROR_NAME
    ) ?? false
  );
}

function isSafariLoadFailed(event: Event, hint: EventHint): boolean {
  const error: unknown = hint?.originalException;

  if (
    error instanceof TypeError &&
    SAFARI_LOAD_FAILED_PATTERN.test(error.message)
  ) {
    return true;
  }

  return (
    event.exception?.values?.some(
      (exception) =>
        exception.type === "TypeError" &&
        SAFARI_LOAD_FAILED_PATTERN.test(exception.value ?? "")
    ) ?? false
  );
}

/**
 * Drops noisy client-side transport failures already tracked via http_client_duration
 * metrics and fetch breadcrumbs. Does not affect Sentry.metrics.* calls.
 */
function shouldDropStaleChunkError(event: Event, hint: EventHint): boolean {
  const error: unknown = hint?.originalException;

  if (isStaleChunkError(error)) {
    if (wasStaleChunkReloadTriggered() || reloadForStaleChunk()) {
      return true;
    }
  }

  const messages: string[] = collectErrorMessages(event, hint);

  for (const message of messages) {
    if (isStaleChunkError(message)) {
      if (wasStaleChunkReloadTriggered() || reloadForStaleChunk()) {
        return true;
      }
    }
  }

  return false;
}

export function shouldDropSentryError(event: Event, hint: EventHint): boolean {
  if (shouldDropStaleChunkError(event, hint)) {
    return true;
  }

  if (isAbortError(event, hint) || isSafariLoadFailed(event, hint)) {
    return true;
  }

  const messages: string[] = collectErrorMessages(event, hint);

  for (const message of messages) {
    if (RESIZE_OBSERVER_LOOP_LIMIT_PATTERN.test(message)) {
      return true;
    }

    if (RESIZE_OBSERVER_LOOP_COMPLETED_PATTERN.test(message)) {
      return true;
    }

    if (HTTP_ERROR_RESPONSE_PATTERN.test(message)) {
      return true;
    }

    if (HTTP_FAILURE_STATUS_ZERO_PATTERN.test(message)) {
      return true;
    }

    if (HTTP_FAILURE_STATUS_IGNORABLE_PATTERN.test(message)) {
      return true;
    }

    if (HTTP_FAILURE_STATUS_SERVER_ERROR_PATTERN.test(message)) {
      return true;
    }

    if (messageMatchesBenignNetworkFailure(message)) {
      return true;
    }
  }

  return false;
}
